// resources/js/components/bid_limits/useBidLimits.ts

import { useEffect } from 'react'
import { batchResolveBidLimits } from './api'

interface RowStore {
    items: any[]
    setItems: (updater: any) => void
}

/**
 * Resolves bid limits for the rows currently in the table store and merges the
 * result onto each row as `bid_limits`. A row that has not been resolved yet
 * carries `bid_limits === undefined`; this hook fills those in one batched
 * (chunked) request per load.
 *
 * Self-correcting: a fresh load brings rows without `bid_limits`, which
 * re-triggers a fetch. Once every row has a value the effect is a no-op, so
 * merging the result back into the store does not loop.
 */
export const useBidLimits = (store: RowStore, parentType: number, keyField: string): void => {
    const missing = store.items.filter((row) => row.bid_limits === undefined)
    const missingKey = missing
        .map((row) => String(row[keyField]))
        .sort()
        .join(',')

    useEffect(() => {
        if (missing.length === 0) {
            return
        }

        let cancelled = false
        const ids = missing.map((row) => String(row[keyField]))

        batchResolveBidLimits(parentType, ids)
            .then((limits) => {
                if (cancelled) {
                    return
                }
                store.setItems((curr: any[]) =>
                    curr.map((row) => {
                        if (row.bid_limits !== undefined) {
                            return row
                        }
                        return { ...row, bid_limits: limits[String(row[keyField])] ?? null }
                    }),
                )
            })
            .catch(() => {
                /* leave bid_limits undefined; the cell shows a neutral placeholder */
            })

        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [missingKey, parentType])
}
